// Service Page Template - Reusable template for all service pages
import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Clock, Users, MapPin, Star } from "lucide-react";
import Navigation from "../shared/Navigation";
import Footer from "../shared/Footer";
import PageBreadcrumb from "../shared/PageBreadcrumb";
import { 
  SectionLabel, 
  SectionHeading, 
  ButtonPrimary, 
  ContactInfoBlock,
  RelatedServices,
  AreasServed 
} from "../shared/SharedComponents";
import { usePageMeta, generateServiceSchema, SchemaScript, generateFAQSchema } from "../shared/SEOHelper";

// All services data for related services linking
export const allServices = [
  { slug: "yoga-classes/hatha-yoga", title: "Hatha Yoga", shortDesc: "Traditional yoga for strength, flexibility and calm." },
  { slug: "yoga-classes/yin-yoga", title: "Yin Yoga", shortDesc: "Slow, deep stretches for release and relaxation." },
  { slug: "yoga-classes/aerial-yoga", title: "Aerial Yoga", shortDesc: "Supported yoga using fabric hammocks." },
  { slug: "yoga-classes/private-yoga", title: "Private Sessions", shortDesc: "Personalised 1-to-1 yoga tailored to you." },
  { slug: "yoga-classes/children-family-yoga", title: "Children's Yoga", shortDesc: "Fun, engaging yoga for kids and families." },
  { slug: "womens-circles/frome", title: "Women's Circles", shortDesc: "Sacred gatherings for connection and support." },
  { slug: "treatments/holistic-treatments", title: "Holistic Treatments", shortDesc: "Massage, Reiki, and energy healing." },
  { slug: "treatments/reiki-healing", title: "Reiki Healing", shortDesc: "Gentle energy work for balance and wellbeing." },
  { slug: "treatments/chakra-healing", title: "Chakra Healing", shortDesc: "Working with energy centres for harmony." },
  { slug: "treatments/swedish-massage", title: "Swedish Massage", shortDesc: "Classic massage for relaxation and tension relief." },
];

const ServicePageTemplate = ({ 
  // SEO
  pageTitle,
  metaDescription,
  canonical,
  
  // Hero
  heroLabel,
  heroTitle,
  heroDescription,
  heroImage,
  
  // Main content
  introTitle,
  introText,
  
  // What's included / benefits
  benefits,
  
  // Who is it for
  idealFor,
  
  // What to expect
  whatToExpect,
  
  // Testimonials
  testimonials,
  
  // FAQ
  faqs,
  
  // Related
  currentSlug,
  
  // Schema
  serviceType,
  serviceName,
  serviceDescription
}) => {
  // SEO
  usePageMeta({
    title: pageTitle,
    description: metaDescription,
    canonical: canonical
  });
  
  const serviceSchema = generateServiceSchema({
    type: serviceType,
    name: serviceName,
    description: serviceDescription
  });
  
  const faqSchema = faqs ? generateFAQSchema(faqs) : null;

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      {faqSchema && <SchemaScript schema={faqSchema} />}
      <Navigation />
      <PageBreadcrumb currentPage={serviceName || heroTitle} currentSlug={currentSlug} />
      
      {/* Hero Section */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <SectionLabel>{heroLabel}</SectionLabel>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
                {heroTitle}
              </h1>
              <p className="text-neutral-700 leading-relaxed mb-6">
                {heroDescription}
              </p>
              <div className="flex flex-wrap gap-4">
                <ButtonPrimary href="/#contact">
                  Enquire & Book
                  <ArrowRight className="w-4 h-4" />
                </ButtonPrimary>
              </div>
              
              {/* Quick Info */}
              <div className="flex flex-wrap gap-4 mt-6 text-sm text-neutral-600">
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gold" />
                  Small groups
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold" />
                  Frome, Somerset
                </span>
              </div>
            </motion.div>
            
            {heroImage && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="relative"
              >
                <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                  <img 
                    src={heroImage} 
                    alt={heroTitle}
                    className="w-full h-full object-cover"
                    width={600}
                    height={450}
                    loading="eager"
                    decoding="async"
                  />
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </section>
      
      {/* Introduction Section */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              {introTitle}
            </h2>
            <div className="prose-content space-y-4">
              {introText.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Benefits Section */}
      {benefits && (
        <section className="py-16 px-6 md:px-12 bg-muted">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
                Benefits
              </h2>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  {benefit.link ? (
                    <Link 
                      to={benefit.link} 
                      className="block bg-white/70 p-6 rounded-xl border border-gold/10 hover:border-gold/30 transition-all group h-full"
                    >
                      <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                        <Check className="w-5 h-5 text-gold" />
                      </div>
                      <h3 className="font-heading text-lg text-neutral-800 mb-2 group-hover:text-gold transition-colors">{benefit.title}</h3>
                      <p className="text-neutral-600 text-sm">{benefit.description}</p>
                      <span className="text-gold text-sm font-medium inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
                        Learn more <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  ) : (
                    <div className="bg-white/70 p-6 rounded-xl border border-gold/10 h-full">
                      <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                        <Check className="w-5 h-5 text-gold" />
                      </div>
                      <h3 className="font-heading text-lg text-neutral-800 mb-2">{benefit.title}</h3>
                      <p className="text-neutral-600 text-sm">{benefit.description}</p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
      
      {/* Who Is It For */}
      {idealFor && (
        <section className="py-16 px-6 md:px-12 bg-cream">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
                Who Is This For?
              </h2>
              <div className="bg-white/70 p-6 md:p-8 rounded-xl border border-gold/10">
                <ul className="space-y-3">
                  {idealFor.map((item, index) => (
                    <li key={index} className="flex items-start gap-3 text-neutral-700">
                      <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* What to Expect */}
      {whatToExpect && (
        <section className="py-16 px-6 md:px-12 bg-muted">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6 text-center">
                What to Expect
              </h2>
              <div className="prose-content space-y-4">
                {whatToExpect.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* FAQ Section */}
      {faqs && faqs.length > 0 && (
        <section className="py-16 px-6 md:px-12 bg-cream">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                    <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                    <p className="text-neutral-600 text-sm">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Testimonials Section */}
      {testimonials && testimonials.length > 0 && (
        <section className="py-16 px-6 md:px-12 bg-muted">
          <div className="max-w-3xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
                What People Say
              </h2>
              <div className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white/70 p-6 rounded-xl border border-gold/10">
                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 text-gold fill-gold" />
                      ))}
                    </div>
                    <p className="text-neutral-700 italic mb-4">"{testimonial.quote}"</p>
                    <div className="border-t border-gold/10 pt-3">
                      <p className="font-medium text-neutral-800 text-sm">{testimonial.name}</p>
                      {testimonial.location && (
                        <p className="text-neutral-500 text-xs">{testimonial.location}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>
      )}
      
      {/* Retreat Promotion Section */}
      <section className="py-12 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-br from-gold/10 to-gold/5 p-6 md:p-8 rounded-2xl border border-gold/20 text-center"
          >
            <h3 className="font-heading text-xl md:text-2xl text-neutral-800 mb-3">
              Want a Deeper Experience?
            </h3>
            <p className="text-neutral-600 mb-5 max-w-lg mx-auto">
              Explore our yoga retreats — from day retreats in Frome to international yoga holidays. 
              Immerse yourself in yoga, sound healing, and holistic wellness.
            </p>
            <Link 
              to="/retreats/yoga-retreats-uk"
              className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-white px-6 py-2.5 rounded-full font-medium transition-colors text-sm"
            >
              Explore Yoga Retreats UK
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
                Ready to Begin?
              </h2>
              <p className="text-neutral-600 mb-6">
                Get in touch to book your session or ask any questions. Annelie Lily will reply within 24-48 hours.
              </p>
              <ButtonPrimary href="/#contact">
                Enquire & Book
                <ArrowRight className="w-4 h-4" />
              </ButtonPrimary>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <ContactInfoBlock showHeading={false} />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Related Services */}
      <RelatedServices services={allServices} currentSlug={currentSlug} />
      
      {/* Areas Served */}
      <AreasServed />
      
      <Footer />
    </div>
  );
};

export default ServicePageTemplate;
